import { Button, ButtonVariants } from '@components/button/Button';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import { centsToWholeDollar } from '@utils/currency';
import { useEffect, useRef, useState } from 'react';
import { checkAuth } from '../auth/apiHelpers';
import {
  AuthModalTypes,
  useAuthModalContext,
} from '../context/AuthModalContext';
import { useAvailabilityContext } from '../context/AvailabilityContext';
import { useOfferCheckoutContext } from '../context/OfferCheckoutContext';
import { useRestaurantContext } from '../context/RestaurantContext';
import { useUserContext } from '../context/UserContext';
import { useReservationDetails } from '../hooks/useReservationDetails';
import styles from './CheckoutButton.scss';
import {
  sendGa4StartOfferEvent,
  sendGa4StartReservationEvent,
} from './gaHelpers';
import { useNavigateToCheckout } from './useNavigateToCheckout';

const NO_PRICE = 0;

export interface CheckoutButtonProps {
  isOffer: boolean;
}

export const CheckoutButton = ({ isOffer }: CheckoutButtonProps) => {
  const { setIsAuthenticated, clearUserState } = useUserContext();
  const { openModal } = useAuthModalContext();
  const { selectedAvailability, selectedDate, selectedGuestCount } =
    useAvailabilityContext();
  const { setOfferCheckout } = useOfferCheckoutContext();
  const { restaurantDetails } = useRestaurantContext();
  const { navigateToCheckout } = useNavigateToCheckout({ isOffer });
  const { data: reservation } = useReservationDetails();
  const [isPending, setIsPending] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  if (!selectedAvailability) throw new Error('selectedAvailability is null');

  const isEditingReservation = !!reservation;

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [ref.current]);

  const navigateToOfferCheckout = () => {
    setOfferCheckout({
      date: selectedAvailability.date,
      guestCount: selectedGuestCount,
      listingId: selectedAvailability.listingId,
      publicName: selectedAvailability.publicName,
      time: selectedAvailability.time,
    });
    navigateToCheckout();
  };

  const handleCheckoutClick = async () => {
    // An availability with a null purchase represent a table that the restaurant is not selling
    // (e.g. a resell table). Even though there might be an event at this time this return is still
    // valid since we don't want to end up in the checkout flow for a table that is not for sale.
    if (selectedAvailability.purchasePrice === null) return;

    setIsPending(true);
    sendGa4StartReservationEvent({
      date: toISODateFormat(selectedDate),
      guestCount: selectedGuestCount,
      price: selectedAvailability.purchasePrice,
      restaurantName: restaurantDetails.name,
      publicName: selectedAvailability.publicName,
      time: selectedAvailability.time,
    });

    const response = await checkAuth();
    setIsPending(false);

    if (response.isAuthenticated) {
      setIsAuthenticated(true);
      navigateToCheckout();
    } else {
      clearUserState();
      openModal(AuthModalTypes.SIGN_IN, {
        onAuthSuccess: () => navigateToCheckout(),
      });
    }
  };

  const handleMakeOfferClick = async () => {
    setIsPending(true);
    sendGa4StartOfferEvent({
      date: selectedAvailability.date,
      restaurantName: restaurantDetails.name,
      publicName: selectedAvailability.publicName,
      time: selectedAvailability.time,
    });

    const response = await checkAuth();
    setIsPending(false);

    if (response.isAuthenticated) {
      setIsAuthenticated(true);
      navigateToOfferCheckout();
    } else {
      clearUserState();
      openModal(AuthModalTypes.SIGN_IN, {
        onAuthSuccess: () => navigateToOfferCheckout(),
      });
    }
  };

  return (
    <div className={styles.ctaContainer} ref={ref}>
      {isOffer ? (
        <Button
          isDisabled={isPending}
          label="Make an Offer"
          onClick={handleMakeOfferClick}
          variant={ButtonVariants.Secondary}
        />
      ) : (
        <Button
          isDisabled={isPending}
          label={
            selectedAvailability.purchasePrice! > NO_PRICE
              ? `${isEditingReservation ? 'Change' : 'Buy'} for ${centsToWholeDollar(selectedAvailability.purchasePrice!)}`
              : isEditingReservation
                ? 'Change Reservation'
                : 'Book Now'
          }
          onClick={handleCheckoutClick}
          variant={ButtonVariants.Primary}
        />
      )}
    </div>
  );
};
